import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Box, ClickAwayListener, Popper } from '@mui/material';
import MilestoneStatusCellRenderer from 'app/pages/loanPipelines/LoanPipelinesTable/common/LoanPipelinesCellRenderer/MilestoneStatusCellRenderer';
import PropTypes from 'prop-types';

import { PAGE_CODES } from 'constants/pageCodes';
import { TagType } from 'constants/tag';
import useLoanId from 'hooks/useLoanId';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { getDocumentsSilentAction } from 'store/documents/actions';
import { getDocuments } from 'store/documents/selectors';
import { getLoanBundleHistoryAction } from 'store/loanApplications/actions';
import { selectLoanBundleHistorySortedByDate } from 'store/loanApplications/selectors';
import { getLoanSummaryProgressAction } from 'store/loanPipelines/actions';
import { selectLoanPipelineSummaryDataProgress } from 'store/loanPipelines/selectors';
import { selectMilestonePresetationSettings } from 'store/milestoneDefinitions/selectors';
import { getDocumentTypesAction } from 'store/system/actions';
import { getDocumentTypes } from 'store/system/selectors';

import {
  popperModifiers,
  SCROLL_DEBOUNCE_MS,
} from '../MainHeaderContent/HeaderViewList/HeaderViewList.constants';

import MilestoneRoadmap from './components/MilestoneRoadmap';
import { MILESTONE_ROADMAP_POPPER_ID } from './MilestoneRoadmapWidget.constants';
import { anchorStyles, popperStyles } from './MilestoneRoadmapWidget.styles';
import { buildMilestoneDocumentsByDefinitionId } from './MilestoneRoadmapWidget.utils';

const MilestoneRoadmapWidget = ({ milestoneStatusName = null }) => {
  const { applicationId } = useParams();
  const loanId = useLoanId();
  const dispatch = useDispatch();
  const progress = useSelector(selectLoanPipelineSummaryDataProgress);
  const presentationSettings = useSelector(selectMilestonePresetationSettings);
  const documents = useSelector(getDocuments);
  const documentTypes = useSelector(getDocumentTypes);
  const deliveryRequests = useSelector(selectLoanBundleHistorySortedByDate);
  const { isViewAllowed: isDocumentsViewAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.DOCUMENTS),
  );
  const { isViewAllowed: isFormDeliveryViewAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.FORM_DELIVERY),
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [isPopperHover, setIsPopperHover] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const closeTimeoutRef = useRef(null);
  const fetchedForIdRef = useRef(null);

  const milestoneValue = useMemo(() => ({ name: milestoneStatusName }), [milestoneStatusName]);

  const documentsByDefinitionId = useMemo(
    () =>
      isDocumentsViewAllowed
        ? buildMilestoneDocumentsByDefinitionId({
            presentationSettings,
            documents,
            documentTypes,
            deliveryRequests,
          })
        : {},
    [isDocumentsViewAllowed, presentationSettings, documents, documentTypes, deliveryRequests],
  );

  const fetchProgress = useCallback(async () => {
    if (!applicationId || fetchedForIdRef.current === applicationId) {
      return;
    }

    setIsLoading(true);

    try {
      // catchAsyncActionError surfaces the error toast then re-rejects, so await
      // here and swallow it — leaving fetchedForIdRef unset keeps the popover on
      // the skeleton and re-fetches on the next hover. Documents + delivery
      // requests feed the per-status documents area and fail soft: the roadmap
      // itself only needs the progress call. The documents fetch stays minimal
      // (no extraction fields, no deleted) and permission-scoped like the tabs.
      const [progressResult] = await Promise.allSettled([
        dispatch(getLoanSummaryProgressAction({ applicationId })),
        ...(isDocumentsViewAllowed
          ? [
              dispatch(
                getDocumentsSilentAction({
                  loanIdOrLegacyLoanNumber: loanId,
                  applicationId,
                  includeDeleted: false,
                  includeFieldsAndResults: false,
                }),
              ),
              dispatch(getDocumentTypesAction()),
            ]
          : []),
        ...(isDocumentsViewAllowed && isFormDeliveryViewAllowed
          ? [dispatch(getLoanBundleHistoryAction({ loanId, applicationId }))]
          : []),
      ]);

      if (progressResult.status === 'rejected') {
        throw progressResult.reason;
      }

      fetchedForIdRef.current = applicationId;
    } catch {
      // handled by catchAsyncActionError; nothing to do here
    } finally {
      setIsLoading(false);
    }
  }, [applicationId, loanId, isDocumentsViewAllowed, isFormDeliveryViewAllowed, dispatch]);

  const onMouseEnter = useCallback(
    e => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }

      setAnchorEl(e.currentTarget);
      fetchProgress();
    },
    [fetchProgress],
  );

  const onMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => setAnchorEl(null), SCROLL_DEBOUNCE_MS);
  }, []);

  const onPopperClickAway = useCallback(() => {
    setAnchorEl(null);
    setIsPopperHover(false);
  }, []);

  const onPopperEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    setIsPopperHover(true);
  }, []);

  const onPopperLeave = useCallback(() => {
    setIsPopperHover(false);
    closeTimeoutRef.current = setTimeout(() => setAnchorEl(null), SCROLL_DEBOUNCE_MS);
  }, []);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    },
    [],
  );

  const isReadyForCurrentLoan = fetchedForIdRef.current === applicationId;

  return (
    <Box sx={anchorStyles} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <MilestoneStatusCellRenderer isNameKey type={TagType.SMALL_FAT} value={milestoneValue} />
      {(!!anchorEl || isPopperHover) && (
        <ClickAwayListener onClickAway={onPopperClickAway}>
          <Popper
            open
            anchorEl={anchorEl}
            id={MILESTONE_ROADMAP_POPPER_ID}
            modifiers={popperModifiers}
            placement="bottom-start"
            sx={popperStyles}
            onMouseEnter={onPopperEnter}
            onMouseLeave={onPopperLeave}
          >
            <MilestoneRoadmap
              documentsByDefinitionId={documentsByDefinitionId}
              isLoading={isLoading || !isReadyForCurrentLoan}
              progress={progress}
            />
          </Popper>
        </ClickAwayListener>
      )}
    </Box>
  );
};

MilestoneRoadmapWidget.propTypes = {
  milestoneStatusName: PropTypes.string,
};

export default MilestoneRoadmapWidget;
